import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';

interface PageTitleContextValue {
  /** The winning page-supplied title override, or null → use the URL-derived label. */
  overrideLabel: string | null;
  /** Register/update a caller's override under a stable id. */
  registerOverride: (id: string, label: string | null) => void;
  /** Remove a caller's override (on unmount). */
  unregisterOverride: (id: string) => void;
}

const PageTitleContext = createContext<PageTitleContextValue | undefined>(undefined);

/**
 * ECOPD-17017 — holds an optional page-level browser-tab-title override so detail
 * pages can show the loaded entity's name (e.g. "VET1") in the tab instead of the
 * generic URL-derived label (e.g. "Run 123"). `usePageTitle` reads this override
 * and prefers it over the path-derived label.
 *
 * Overrides are kept as an ordered registry keyed by caller id rather than a
 * single slot: if two mounted components each call `useDocumentTitle` (e.g. a
 * page-level call plus `ActiveTabSubHeader`), the most-recently-registered one
 * wins, and one component unmounting only removes ITS entry — it can no longer
 * clear a still-live sibling's title (which previously caused a transient flash
 * back to the URL-derived title).
 *
 * Must sit ABOVE the component that calls `usePageTitle` (AppLayout) so that hook
 * can read the override via context.
 */
export const PageTitleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [entries, setEntries] = useState<Array<{ id: string; label: string }>>([]);

  const registerOverride = useCallback((id: string, label: string | null) => {
    setEntries((prev) => {
      const without = prev.filter((e) => e.id !== id);
      return label ? [...without, { id, label }] : without;
    });
  }, []);

  const unregisterOverride = useCallback((id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  // Most-recently-registered non-null override wins.
  const overrideLabel = entries.length > 0 ? entries[entries.length - 1].label : null;

  const value = useMemo(
    () => ({ overrideLabel, registerOverride, unregisterOverride }),
    [overrideLabel, registerOverride, unregisterOverride],
  );
  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
};

/** Internal — the current winning override label, consumed by `usePageTitle`. */
export const usePageTitleOverride = (): string | null => {
  const ctx = useContext(PageTitleContext);
  return ctx?.overrideLabel ?? null;
};

/**
 * ECOPD-17017 — set the browser tab title to a specific entity name for the
 * lifetime of the calling component. Pass the loaded name (e.g. `run.public_id`);
 * pass null/undefined/'' while it is still loading (the URL-derived label shows
 * until then). The override is removed on unmount so it never leaks onto the next
 * page/tab. Multiple concurrent callers are supported (see PageTitleProvider).
 */
export const useDocumentTitle = (label: string | null | undefined): void => {
  const ctx = useContext(PageTitleContext);
  const id = useId();
  const registerOverride = ctx?.registerOverride;
  const unregisterOverride = ctx?.unregisterOverride;
  useEffect(() => {
    if (!registerOverride || !unregisterOverride) return;
    const trimmed = typeof label === 'string' ? label.trim() : '';
    registerOverride(id, trimmed ? trimmed : null);
    return () => unregisterOverride(id);
  }, [id, label, registerOverride, unregisterOverride]);
};

export default PageTitleProvider;
